/**
 * useCreateInvitation — TanStack mutation over
 * `POST /organizations/:slug/invitations`.
 *
 * Body: `{ email, role_id }`. Response (201): `{ id, expires_at }`.
 *
 * Error envelope (tagged union):
 *   - 'unprocessable' — 422; carries `reason` (self_invite / already_member /
 *                       pending_exists / role_not_found / invalid_email) so
 *                       the wizard can map to a specific message.
 *   - 'forbidden'    — 403 (missing identity.invitations.create).
 *   - 'notFound'     — 404 (organization not found).
 *   - 'error'        — anything else.
 *
 * On success, invalidates the invitations list AND the audit query for
 * the current user — `user.invited` events should surface immediately
 * if the actor is viewing their own audit feed.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';

export interface CreateInvitationInput {
  readonly email: string;
  readonly roleId: string;
  readonly firstName?: string;
  readonly lastName?: string;
}

export interface CreateInvitationResult {
  readonly id: string;
  readonly expiresAt: string;
}

export type CreateInvitationError =
  | { readonly kind: 'unprocessable'; readonly reason: string }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'error' };

export function buildInvitationsListQueryKey(
  orgSlug: string | null,
): readonly unknown[] {
  return ['identity', 'invitations', orgSlug];
}

export interface UseCreateInvitationResult {
  readonly mutateAsync: (
    input: CreateInvitationInput,
  ) => Promise<CreateInvitationResult>;
  readonly isPending: boolean;
}

export function useCreateInvitation(): UseCreateInvitationResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  const mutation = useMutation<
    CreateInvitationResult,
    CreateInvitationError,
    CreateInvitationInput
  >({
    mutationFn: async (input) => {
      if (orgSlug === null) {
        throw { kind: 'error' } satisfies CreateInvitationError;
      }
      const body: Record<string, unknown> = {
        email: input.email,
        role_id: input.roleId,
      };
      if (input.firstName !== undefined && input.firstName.length > 0) {
        body.first_name = input.firstName;
      }
      if (input.lastName !== undefined && input.lastName.length > 0) {
        body.last_name = input.lastName;
      }
      const { response, data, error } = await apiClient.POST(
        '/organizations/{slug}/invitations',
        {
          params: { path: { slug: orgSlug } },
          body: body as never,
        },
      );
      if (response.status === 422) {
        const body = (error ?? data) as { message?: unknown } | undefined;
        const reason =
          typeof body?.message === 'string' ? body.message : 'unprocessable';
        throw {
          kind: 'unprocessable',
          reason,
        } satisfies CreateInvitationError;
      }
      if (response.status === 403) {
        throw { kind: 'forbidden' } satisfies CreateInvitationError;
      }
      if (response.status === 404) {
        throw { kind: 'notFound' } satisfies CreateInvitationError;
      }
      if (!response.ok || data === undefined) {
        throw { kind: 'error' } satisfies CreateInvitationError;
      }
      const dto = data as { id: string; expires_at: string };
      return { id: dto.id, expiresAt: dto.expires_at };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: buildInvitationsListQueryKey(orgSlug),
      });
      // The audit feed of the actor will eventually surface the new
      // `user.invited` event — invalidate broadly so any open audit
      // query refetches. Keyed by orgSlug + 'audit' prefix.
      await queryClient.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          return (
            Array.isArray(k) &&
            k[0] === 'identity' &&
            k[2] === orgSlug &&
            k.includes('audit')
          );
        },
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
